<div class="modal-header">
  <div class="d-flex flex-column">
    <h4 class="modal-title mb-1">
      <i class="fa fa-user-plus text-primary me-2"></i>
      {{ 'companyProfile.membershipPlans.editMember.addFamilyMember' | translate }}
    </h4>
    <p class="text-muted small mb-0">{{ 'common.requiredFieldsHint' | translate }}</p>
  </div>
  <button type="button" class="btn-close" [attr.aria-label]="'common.close' | translate" (click)="activeModal.dismiss()"></button>
</div>

<div class="modal-body">
  <!-- Member Type Selection -->
  <div class="member-type-selector mb-5">
    <div class="d-flex gap-3">
      <div
        class="type-option flex-fill"
        [class.active]="memberType === 'existing'"
        (click)="memberType = 'existing'; onMemberTypeChange()"
      >
        <i class="fa fa-user-check"></i>
        <span>{{ 'companyProfile.membershipPlans.addFamilyMember.existingUser' | translate }}</span>
        <small>{{ 'companyProfile.membershipPlans.addFamilyMember.existingUserHint' | translate }}</small>
      </div>
      <div
        class="type-option flex-fill"
        [class.active]="memberType === 'child'"
        (click)="memberType = 'child'; onMemberTypeChange()"
      >
        <i class="fa fa-child"></i>
        <span>{{ 'companyProfile.membershipPlans.addFamilyMember.addChild' | translate }}</span>
        <small>{{ 'companyProfile.membershipPlans.addFamilyMember.addChildHint' | translate }}</small>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  @if (errorMessage) {
    <div class="alert alert-danger mb-4">
      <i class="fa fa-exclamation-triangle me-2"></i>
      {{ errorMessage }}
    </div>
  }

  <!-- Existing User Search -->
  @if (memberType === 'existing') {
    <div class="form-group mb-4">
      <label class="form-label">{{ 'companyProfile.membershipPlans.addMember.searchUser' | translate }} <span class="text-danger">*</span></label>
      @if (selectedUser) {
        <div class="selected-user-card">
          <div class="d-flex align-items-center">
            <div class="symbol symbol-40px me-3">
              <span class="symbol-label bg-light-primary text-primary fw-bold">
                {{ selectedUser.first_name?.charAt(0) || 'U' }}
              </span>
            </div>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ selectedUser.full_name || selectedUser.first_name + ' ' + selectedUser.last_name }}</div>
              <div class="text-muted small">{{ selectedUser.email }}</div>
            </div>
            <button type="button" class="btn btn-sm btn-light-danger" (click)="clearSelectedUser()">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
      } @else {
        <div class="position-relative">
          <input
            type="text"
            class="form-control"
            [class.is-invalid]="isSelectedUserInvalid"
            [(ngModel)]="userSearchQuery"
            (ngModelChange)="onUserSearch()"
            [placeholder]="'companyProfile.membershipPlans.addMember.searchPlaceholder' | translate"
          />
          @if (isSearching) {
            <div class="search-spinner">
              <i class="fa fa-spinner fa-spin"></i>
            </div>
          }
        </div>
        @if (userSearchResults.length > 0) {
          <div class="search-results mt-2">
            @for (user of userSearchResults; track user.user_id) {
              <div class="search-result-item" (click)="selectUser(user)">
                <div class="symbol symbol-30px me-2">
                  <span class="symbol-label bg-light text-primary fw-bold">
                    {{ user.first_name?.charAt(0) || 'U' }}
                  </span>
                </div>
                <div>
                  <div class="fw-semibold">{{ user.full_name || user.first_name + ' ' + user.last_name }}</div>
                  <div class="text-muted small">{{ user.email }}</div>
                </div>
              </div>
            }
          </div>
        }
        @if (userSearchQuery.length >= 2 && userSearchResults.length === 0 && !isSearching) {
          <div class="text-muted small mt-2">
            <i class="fa fa-info-circle me-1"></i>
            {{ 'companyProfile.membershipPlans.addMember.noUsersFound' | translate:{ query: userSearchQuery } }}
          </div>
        }
      }
    </div>
  }

  <!-- Child Entry -->
  @if (memberType === 'child') {
    <div class="row g-3 mb-4">
      <div class="col-6">
        <label class="form-label">{{ 'companyProfile.membershipPlans.addMember.firstName' | translate }} <span class="text-danger">*</span></label>
        <input
          type="text"
          class="form-control"
          [class.is-invalid]="isChildFirstNameInvalid"
          [(ngModel)]="childFirstName"
          [placeholder]="'companyProfile.membershipPlans.addMember.firstNamePlaceholder' | translate"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'companyProfile.membershipPlans.addMember.lastName' | translate }} <span class="text-danger">*</span></label>
        <input
          type="text"
          class="form-control"
          [class.is-invalid]="isChildLastNameInvalid"
          [(ngModel)]="childLastName"
          [placeholder]="'companyProfile.membershipPlans.addMember.lastNamePlaceholder' | translate"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'companyProfile.membershipPlans.addFamilyMember.dateOfBirth' | translate }} <span class="text-danger">*</span></label>
        <input
          type="date"
          class="form-control"
          [class.is-invalid]="isChildDateOfBirthInvalid"
          [(ngModel)]="childDateOfBirth"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ 'companyProfile.membershipPlans.addFamilyMember.ridingClub' | translate }}</label>
        <input
          type="text"
          class="form-control"
          [(ngModel)]="childRidingClub"
          [placeholder]="'companyProfile.membershipPlans.addFamilyMember.ridingClubPlaceholder' | translate"
        />
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  <app-button
    [variant]="'ghost'"
    [label]="'common.cancel' | translate"
    (clicked)="activeModal.dismiss()"
  />
  <app-button
    [variant]="'primary'"
    [label]="isSubmitting ? ('common.adding' | translate) : ('common.add' | translate)"
    [iconClass]="isSubmitting ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"
    [disabled]="isSubmitting"
    (clicked)="submit()"
  />
</div>
